import React, { useState, useEffect, useRef } from 'react';
import { collection, addDoc, updateDoc, doc, writeBatch, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { subscribeProductItems, productWriteFields, productDeleteFields } from '../../utils/productCatalog';
import { getLiveDoc } from '../../utils/liveData';
import { useAuth } from '../../hooks/useAuth';
import { PRODUCT_TYPES, STOR_ZEBRA_EXTRAS } from '../../constants';
import { formatPrice, normalizeSearchText, getStorZebraExtraFields, getStorZebraExtraOptions, normalizeStorZebraExtraCompanies } from '../../utils';
import * as XLSX from 'xlsx';
import './ProductList.css';
import './ProductListModern.css';

// Motor ayarları: boş bir firma kutusu / 3 firmalık boş liste
const emptyMotorCompany = () => ({ name: '', motorPrice: '', motorCost: '', kumandaPrice: '', kumandaCost: '', currency: 'USD', rate: '' });
const emptyMotorCompanies = () => [emptyMotorCompany(), emptyMotorCompany(), emptyMotorCompany()];

// Pile Ek Ücret kartındaki satırlar. Sadece müşteriye yansıyan birim fiyat girilir;
// atölye dikim maliyetleri Giderler > Atölye Giderleri'nden elle takip edilir.
const PILE_EXTRA_ROWS = [
  { field: 'standartDikim', label: 'Standart Dikim Ücreti' },
  { field: 'sPile', label: 'S Pile' },
  { field: 'american2', label: "Amerikan 2'li" },
  { field: 'american3', label: "Amerikan 3'lü" },
  { field: 'kapsul', label: 'Kapsül Pile' },
  { field: 'altEtekKatlama', label: 'Alt Etek Katlama', tulOnly: true },
];

const DualPriceCostInput = ({ label, costValue, priceValue, onCostChange, onPriceChange, costPlaceholder = '0.00', costLabel = 'Maliyet', showCost = true }) => (
  <div className="dual-price-cost-group" style={{ marginBottom: 0 }}>
    {label && <label className="dual-input-main-label">{label}</label>}
    <div className="dual-input-container">
      {showCost && <div className="input-with-small-label">
        <small>{costLabel}</small>
        <div className="input-icon-wrapper">
          <input 
            type="number" 
            placeholder={costPlaceholder} 
            value={costValue} 
            onChange={onCostChange} 
          />
          <span className="currency-symbol">₺</span>
        </div>
      </div>}
      <div className="input-with-small-label">
        <small>Birim Fiyat</small>
        <div className="input-icon-wrapper">
          <input 
            type="number" 
            placeholder="0.00" 
            value={priceValue} 
            onChange={onPriceChange} 
          />
          <span className="currency-symbol">₺</span>
        </div>
      </div>
    </div>
  </div>
);

const MotorCompanyBox = ({ type, index, company, onCompanyFieldChange, exchangeRates }) => {
  const currentRate = exchangeRates[company.currency] || 1;
  const symbol = company.currency === 'USD' ? '$' : '€';

  return (
    <div className="motor-company-box">
      <div className="input-with-small-label motor-company-name">
        <small>{index + 1}. Firma Adı</small>
        <input
          type="text"
          value={company.name}
          onChange={(e) => onCompanyFieldChange(type, index, 'name', e.target.value)}
          placeholder={`örn. ${index + 1}. Motor Firması`}
        />
      </div>
      <div className="settings-grid">
        <div className="input-with-small-label">
          <small>Motor Maliyeti ({company.currency})</small>
          <div className="input-icon-wrapper">
            <input
              type="number"
              step="0.01"
              value={company.motorCost}
              onChange={(e) => onCompanyFieldChange(type, index, 'motorCost', e.target.value)}
              placeholder="0.00"
            />
            <span className="currency-symbol">{symbol}</span>
          </div>
        </div>
        <div className="input-with-small-label">
          <small>Motor Birim Fiyat ({company.currency})</small>
          <div className="input-icon-wrapper">
            <input
              type="number"
              step="0.01"
              value={company.motorPrice}
              onChange={(e) => onCompanyFieldChange(type, index, 'motorPrice', e.target.value)}
              placeholder="0.00"
            />
            <span className="currency-symbol">{symbol}</span>
          </div>
        </div>
        <div className="input-with-small-label">
          <small>Kumanda Maliyeti ({company.currency})</small>
          <div className="input-icon-wrapper">
            <input
              type="number"
              step="0.01"
              value={company.kumandaCost}
              onChange={(e) => onCompanyFieldChange(type, index, 'kumandaCost', e.target.value)}
              placeholder="0.00"
            />
            <span className="currency-symbol">{symbol}</span>
          </div>
        </div>
        <div className="input-with-small-label">
          <small>Kumanda Birim Fiyat ({company.currency})</small>
          <div className="input-icon-wrapper">
            <input
              type="number"
              step="0.01"
              value={company.kumandaPrice}
              onChange={(e) => onCompanyFieldChange(type, index, 'kumandaPrice', e.target.value)}
              placeholder="0.00"
            />
            <span className="currency-symbol">{symbol}</span>
          </div>
        </div>
        <div className="input-with-small-label">
          <small>Döviz Cinsi</small>
          <select
            value={company.currency}
            onChange={(e) => onCompanyFieldChange(type, index, 'currency', e.target.value)}
            className="modern-select"
            style={{ width: '100%', height: '38px', borderRadius: '8px', border: '1px solid #e2e8f0', padding: '0 10px', backgroundColor: '#f8fafc' }}
          >
            <option value="USD">USD ($)</option>
            <option value="EUR">EUR (€)</option>
          </select>
        </div>
        <div className="input-with-small-label">
          <small>Özel Kur (Opsiyonel)</small>
          <div className="input-icon-wrapper">
            <input
              type="number"
              step="0.01"
              value={company.rate}
              onChange={(e) => onCompanyFieldChange(type, index, 'rate', e.target.value)}
              placeholder={currentRate}
            />
            <span className="currency-symbol">₺</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const MotorSettingsCard = ({ type, settings, onCompanyFieldChange, onSave, isSaving, exchangeRates, isCollapsed, onToggleCollapse }) => {
  return (
    <div className="settings-card motor-settings-card" style={{ marginBottom: '20px' }}>
      <div
        className="settings-card-title settings-sub-header"
        onClick={onToggleCollapse}
        style={{ cursor: 'pointer', justifyContent: 'space-between' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <i className="fas fa-microchip"></i>
          <h4>Motor & Kumanda & Kur Ayarları</h4>
        </div>
        <i className={`fas fa-chevron-${isCollapsed ? 'down' : 'up'}`} style={{ color: '#64748b', fontSize: '12px' }}></i>
      </div>

      {!isCollapsed && (
        <div style={{ animation: 'slideDown 0.3s ease-out' }}>
          <p className="motor-settings-hint">
            Bu ürün tipi için en fazla 3 farklı motor firması tanımlayabilirsiniz. Firma adını boş bırakırsanız o firma sipariş formunda gösterilmez.
          </p>
          {settings.companies.map((company, index) => (
            <MotorCompanyBox
              key={index}
              type={type}
              index={index}
              company={company}
              onCompanyFieldChange={onCompanyFieldChange}
              exchangeRates={exchangeRates}
            />
          ))}
          <div className="settings-card-actions">
            <button type="button" className="btn-save" onClick={() => onSave()} disabled={isSaving}>
              <i className="fas fa-save"></i> {isSaving ? 'Kaydediliyor...' : 'Ayarları Kaydet'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Bir ürün tipinin firma fiyatı girilen tüm çeşit anahtarları (örn. jumbo, jumbo38, kapaliKasaKucuk...)
const storZebraPriceKeys = (type) => STOR_ZEBRA_EXTRAS[type].flatMap(extra => getStorZebraExtraOptions(extra).map(o => o.key));

const newExtraCompanyId = () => `firma-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

// Stor / Zebra ek ücret ayarları — her firma için her seçeneğin maliyet + birim fiyatı
const StorZebraExtrasCard = ({ type, title, icon, companies, activeId, onSelect, onAdd, onRemove, onRename, onPriceChange, onSave, isSaving, isCollapsed, onToggleCollapse }) => {
  const [confirmRemoveId, setConfirmRemoveId] = useState(null);
  const active = companies.find(c => c.id === activeId) || companies[0];
  const collapsible = typeof onToggleCollapse === 'function';
  const open = !collapsible || !isCollapsed;

  return (
    <div className="settings-card sz-extras-card" style={{ marginBottom: '20px' }}>
      <div
        className={`settings-card-title${collapsible ? ' settings-sub-header' : ''}`}
        onClick={collapsible ? onToggleCollapse : undefined}
        style={collapsible ? { cursor: 'pointer', justifyContent: 'space-between' } : undefined}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <i className={`fas ${icon}`}></i>
          <h4>{title}</h4>
          <span className="sz-firm-count">{companies.length} firma</span>
        </div>
        {collapsible && <i className={`fas fa-chevron-${isCollapsed ? 'down' : 'up'}`} style={{ color: '#64748b', fontSize: '12px' }}></i>}
      </div>

      {open && active && (
        <div style={{ animation: 'slideDown 0.3s ease-out' }}>
          <div className="sz-firm-tabs" role="tablist" aria-label="Ek ücret firmaları">
            {companies.map((company, i) => (
              <button
                key={company.id}
                type="button"
                role="tab"
                aria-selected={company.id === active.id}
                className={`sz-firm-tab${company.id === active.id ? ' is-active' : ''}`}
                onClick={() => { onSelect(type, company.id); setConfirmRemoveId(null); }}
              >
                <span className="sz-firm-dot" data-index={i % 6}></span>
                {company.name.trim() || `Firma ${i + 1}`}
              </button>
            ))}
            <button type="button" className="sz-firm-add" onClick={() => onAdd(type)}>
              <i className="fas fa-plus"></i> Firma Ekle
            </button>
          </div>

          <div className="sz-firm-head">
            <div className="input-with-small-label sz-firm-name">
              <small>Firma Adı</small>
              <input
                type="text"
                value={active.name}
                onChange={(e) => onRename(type, active.id, e.target.value)}
                placeholder="örn. Tedarikçi adı"
              />
            </div>
            {companies.length > 1 && (
              confirmRemoveId === active.id ? (
                <div className="sz-firm-confirm" role="alert">
                  <span>"{active.name.trim() || 'Bu firma'}" silinsin mi?</span>
                  <button type="button" className="sz-btn-danger" onClick={() => { onRemove(type, active.id); setConfirmRemoveId(null); }}>Sil</button>
                  <button type="button" className="sz-btn-ghost" onClick={() => setConfirmRemoveId(null)}>Vazgeç</button>
                </div>
              ) : (
                <button type="button" className="sz-btn-ghost sz-firm-remove" onClick={() => setConfirmRemoveId(active.id)}>
                  <i className="fas fa-trash"></i> Firmayı Sil
                </button>
              )
            )}
          </div>

          {[
            ...STOR_ZEBRA_EXTRAS[type].filter(e => e.variants).map(e => ({ id: e.key, title: e.label, fixed: e.fixed, options: e.variants })),
            { id: 'diger', title: 'Diğer Seçenekler', options: STOR_ZEBRA_EXTRAS[type].filter(e => !e.variants) }
          ].filter(group => group.options.length > 0).map(group => (
            <section key={group.id} className="sz-price-group">
              <h5 className="sz-price-group-title">
                {group.title}
                <span className="sz-price-group-note">
                  {group.id === 'diger' ? 'tek seçenek' : `${group.options.length} çeşit · ${group.fixed ? 'adet başı' : 'en (m) başı'}`}
                </span>
              </h5>
              <div className="settings-grid">
                {group.options.map(({ key, label, fixed }) => (
                  <DualPriceCostInput
                    key={key}
                    label={fixed ? `${label} (adet başı)` : label}
                    costValue={active.prices[key]?.cost ?? ''}
                    priceValue={active.prices[key]?.price ?? ''}
                    onCostChange={(e) => onPriceChange(type, active.id, key, 'cost', e.target.value)}
                    onPriceChange={(e) => onPriceChange(type, active.id, key, 'price', e.target.value)}
                  />
                ))}
              </div>
            </section>
          ))}
          <div className="settings-card-actions">
            <button type="button" className="btn-save" onClick={() => onSave()} disabled={isSaving}>
              <i className="fas fa-save"></i> {isSaving ? 'Kaydediliyor...' : 'Ayarları Kaydet'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Firestore toplu yazma en fazla 500 işlem alır; büyük listeler parçalara bölünür
const BATCH_LIMIT = 450;
const commitInChunks = async (items, addToBatch) => {
  for (let i = 0; i < items.length; i += BATCH_LIMIT) {
    const batch = writeBatch(db);
    items.slice(i, i + BATCH_LIMIT).forEach(item => addToBatch(batch, item));
    await batch.commit();
  }
};

const ProductList = () => {
  const { isAdmin, isSatisci, isMontajci } = useAuth();

  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isExcelLoading, setIsExcelLoading] = useState(false);
  const [activeTypeTab, setActiveTypeTab] = useState(Object.keys(PRODUCT_TYPES)[0]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const fileInputRef = useRef(null);

  const [tulExtraPrices, setTulExtraPrices] = useState({ standartDikim: '', standartDikimCost: '', sPile: '', sPileCost: '', american2: '', american2Cost: '', american3: '', american3Cost: '', kapsul: '', kapsulCost: '', altEtekKatlama: '', altEtekKatlamaCost: '' });
  const [kanatExtraPrices, setKanatExtraPrices] = useState({ standartDikim: '', standartDikimCost: '', sPile: '', sPileCost: '', american2: '', american2Cost: '', american3: '', american3Cost: '', kapsul: '', kapsulCost: '' });
  const [jaluziExtras, setJaluziExtras] = useState({ zigil: '', zigilCost: '' });
  const [metalJaluziExtras, setMetalJaluziExtras] = useState({ zigil: '', zigilCost: '', reduktor: '', reduktorCost: '', ikiCamArasi: '', ikiCamArasiCost: '' });
  const [motorCurrency, setMotorCurrency] = useState('USD');
  const [exchangeRates, setExchangeRates] = useState({ USD: 1, EUR: 1 });
  
  // Ürün tipine özel motor ayarları — her tip için en fazla 3 firma, her firmanın motor + kumanda fiyat/maliyeti
  const [productMotorSettings, setProductMotorSettings] = useState({
    stor: { companies: emptyMotorCompanies() },
    zebra: { companies: emptyMotorCompanies() },
    ahsapJaluzi: { companies: emptyMotorCompanies() },
    metalJaluzi: { companies: emptyMotorCompanies() },
    dikeyPerde: { companies: emptyMotorCompanies() },
    dikeyPvc: { companies: emptyMotorCompanies() },
    dikeyStor: { companies: emptyMotorCompanies() },
    katlamaliPerde: { companies: emptyMotorCompanies() },
  });

  // Stor / Zebra ek ücret firmaları (ilk firma eski tekil fiyatlardır)
  const [extraCompanies, setExtraCompanies] = useState(() => ({
    stor: normalizeStorZebraExtraCompanies('stor'),
    zebra: normalizeStorZebraExtraCompanies('zebra')
  }));
  const [activeExtraCompany, setActiveExtraCompany] = useState({ stor: 'default', zebra: 'default' });
  const [dikeyExtras, setDikeyExtras] = useState({ fon: '', fonCost: '', fonList: [] });
  const [katlamaliExtras, setKatlamaliExtras] = useState({ mekanizmaList: [] });
  const [newDikeyFonName, setNewDikeyFonName] = useState('');
  const [newKatlamaliMekanizma, setNewKatlamaliMekanizma] = useState({ type: '', price: '', cost: '' });
  const [isFonListCollapsed, setIsFonListCollapsed] = useState(true);
  const [isKatlamaliMekanizmaCollapsed, setIsKatlamaliMekanizmaCollapsed] = useState(true);
  const [rustikExtras, setRustikExtras] = useState({
    ayakModelList: [],
    ayakCapList: [],
    ayakUzunlukList: [],
    ayakRenkKoduList: []
  });
  const [newRustikModel, setNewRustikModel] = useState('');
  const [newRustikCap, setNewRustikCap] = useState('');
  const [newRustikUzunluk, setNewRustikUzunluk] = useState('');
  const [newRustikRenk, setNewRustikRenk] = useState('');
  const [isMotorSettingsCollapsed, setIsMotorSettingsCollapsed] = useState(true);
  const [isStorExtrasCollapsed, setIsStorExtrasCollapsed] = useState(true);
  const [isRustikSettingsCollapsed, setIsRustikSettingsCollapsed] = useState(true);
  const [isRustikModelCollapsed, setIsRustikModelCollapsed] = useState(true);
  const [isRustikCapCollapsed, setIsRustikCapCollapsed] = useState(true);
  const [isRustikUzunlukCollapsed, setIsRustikUzunlukCollapsed] = useState(true);
  const [isRustikRenkCollapsed, setIsRustikRenkCollapsed] = useState(true);
  
  const [editingRustikModel, setEditingRustikModel] = useState({ index: null, value: '' });
  const [editingRustikCap, setEditingRustikCap] = useState({ index: null, value: '' });
  const [editingRustikUzunluk, setEditingRustikUzunluk] = useState({ index: null, value: '' });
  const [editingRustikRenk, setEditingRustikRenk] = useState({ index: null, value: '' });

  const updateExtraCompanies = (type, fn) => setExtraCompanies(prev => ({ ...prev, [type]: fn(prev[type]) }));

  const handleExtraCompanyAdd = (type) => {
    const id = newExtraCompanyId();
    updateExtraCompanies(type, list => [...list, {
      id,
      name: `Firma ${list.length + 1}`,
      prices: Object.fromEntries(storZebraPriceKeys(type).map(key => [key, { price: '', cost: '' }]))
    }]);
    setActiveExtraCompany(prev => ({ ...prev, [type]: id }));
  };

  const handleExtraCompanyRemove = (type, id) => {
    const remaining = extraCompanies[type].filter(c => c.id !== id);
    if (remaining.length === 0) return;
    updateExtraCompanies(type, () => remaining);
    setActiveExtraCompany(prev => ({ ...prev, [type]: remaining[0].id }));
  };

  const handleExtraCompanyRename = (type, id, name) => {
    updateExtraCompanies(type, list => list.map(c => (c.id === id ? { ...c, name } : c)));
  };

  const handleExtraCompanyPriceChange = (type, id, key, field, value) => {
    updateExtraCompanies(type, list => list.map(c => (
      c.id === id ? { ...c, prices: { ...c.prices, [key]: { ...c.prices[key], [field]: value } } } : c
    )));
  };

  // Firestore'a yazılacak hali: firmalar + geriye uyumluluk için ilk firmanın fiyatları eski alanlara
  const buildExtraCompaniesUpdate = (type) => {
    const companies = extraCompanies[type].map((c, i) => ({
      id: c.id,
      name: c.name.trim() || `Firma ${i + 1}`,
      prices: Object.fromEntries(storZebraPriceKeys(type).map(key => [key, {
        price: Number(c.prices[key]?.price) || 0,
        cost: Number(c.prices[key]?.cost) || 0
      }]))
    }));
    const legacy = Object.fromEntries(storZebraPriceKeys(type).flatMap(key => {
      const f = getStorZebraExtraFields(type, key);
      return [[f.legacyPrice, companies[0].prices[key].price], [f.legacyCost, companies[0].prices[key].cost]];
    }));
    return { [`${type}ExtraCompanies`]: companies, ...legacy };
  };

  const handleMotorCompanyFieldChange = (type, index, field, value) => {
    setProductMotorSettings(prev => {
      const companies = prev[type].companies.map((c, i) => (i === index ? { ...c, [field]: value } : c));
      return { ...prev, [type]: { ...prev[type], companies } };
    });
  };

  useEffect(() => {
    // Fetch Settings
    const fetchSettings = async () => {
      try {
        const docSnap = await getLiveDoc('settings/global');
        if (docSnap.exists()) {
          const data = docSnap.data();
          setTulExtraPrices({
            standartDikim: data.tulStandartDikimPrice !== undefined ? data.tulStandartDikimPrice : '',
            standartDikimCost: data.tulStandartDikimCost !== undefined ? data.tulStandartDikimCost : '',
            sPile: data.tulSPilePrice !== undefined ? data.tulSPilePrice : (data.sPilePrice || ''),
            sPileCost: data.tulSPileCost !== undefined ? data.tulSPileCost : (data.sPileCost || ''),
            american2: data.tulAmerican2Price !== undefined ? data.tulAmerican2Price : (data.american2Price || ''),
            american2Cost: data.tulAmerican2Cost !== undefined ? data.tulAmerican2Cost : (data.american2Cost || ''),
            american3: data.tulAmerican3Price !== undefined ? data.tulAmerican3Price : (data.american3Price || ''),
            american3Cost: data.tulAmerican3Cost !== undefined ? data.tulAmerican3Cost : (data.american3Cost || ''),
            kapsul: data.tulKapsulPrice !== undefined ? data.tulKapsulPrice : (data.kapsulPrice || ''),
            kapsulCost: data.tulKapsulCost !== undefined ? data.tulKapsulCost : (data.kapsulCost || ''),
            altEtekKatlama: data.tulAltEtekKatlamaPrice !== undefined ? data.tulAltEtekKatlamaPrice : '',
            altEtekKatlamaCost: data.tulAltEtekKatlamaCost !== undefined ? data.tulAltEtekKatlamaCost : '',
          });
          setKanatExtraPrices({
            standartDikim: data.kanatStandartDikimPrice !== undefined ? data.kanatStandartDikimPrice : '',
            standartDikimCost: data.kanatStandartDikimCost !== undefined ? data.kanatStandartDikimCost : '',
            sPile: data.kanatSPilePrice !== undefined ? data.kanatSPilePrice : (data.sPilePrice || ''),
            sPileCost: data.kanatSPileCost !== undefined ? data.kanatSPileCost : (data.sPileCost || ''),
            american2: data.kanatAmerican2Price !== undefined ? data.kanatAmerican2Price : (data.american2Price || ''),
            american2Cost: data.kanatAmerican2Cost !== undefined ? data.kanatAmerican2Cost : (data.american2Cost || ''),
            american3: data.kanatAmerican3Price !== undefined ? data.kanatAmerican3Price : (data.american3Price || ''),
            american3Cost: data.kanatAmerican3Cost !== undefined ? data.kanatAmerican3Cost : (data.american3Cost || ''),
            kapsul: data.kanatKapsulPrice !== undefined ? data.kanatKapsulPrice : (data.kapsulPrice || ''),
            kapsulCost: data.kanatKapsulCost !== undefined ? data.kanatKapsulCost : (data.kapsulCost || ''),
          });
          if (data.motorCurrency) setMotorCurrency(data.motorCurrency);
          setJaluziExtras({
            zigil: data.zigilPrice || '',
            zigilCost: data.zigilCost || ''
          });
          setMetalJaluziExtras({
            zigil: data.metalZigilPrice || '',
            zigilCost: data.metalZigilCost || '',
            reduktor: data.metalReduktorPrice || '',
            reduktorCost: data.metalReduktorCost || '',
            ikiCamArasi: data.ikiCamArasiPrice || '',
            ikiCamArasiCost: data.ikiCamArasiCost || ''
          });
          const storCompanies = normalizeStorZebraExtraCompanies('stor', data);
          const zebraCompanies = normalizeStorZebraExtraCompanies('zebra', data);
          setExtraCompanies({ stor: storCompanies, zebra: zebraCompanies });
          setActiveExtraCompany({ stor: storCompanies[0].id, zebra: zebraCompanies[0].id });
          setDikeyExtras({
            fon: data.dikeyFonPrice || '',
            fonCost: data.dikeyFonCost || '',
            fonList: data.dikeyFonList || []
          });
          setKatlamaliExtras({
            mekanizmaList: data.katlamaliMekanizmaList || []
          });
          setRustikExtras({
            ayakModelList: data.rustikAyakModelList || [],
            ayakCapList: data.rustikAyakCapList || [],
            ayakUzunlukList: data.rustikAyakUzunlukList || [],
            ayakRenkKoduList: data.rustikAyakRenkKoduList || []
          });

          // Ürün bazlı motor ayarlarını yükle (her tip için en fazla 3 firma)
          const motorSettings = { ...productMotorSettings };
          const motorTypes = ['stor', 'zebra', 'ahsapJaluzi', 'metalJaluzi', 'dikeyPerde', 'dikeyPvc', 'dikeyStor', 'katlamaliPerde'];
          motorTypes.forEach(type => {
            const savedCompanies = data[`${type}MotorCompanies`];
            motorSettings[type] = {
              companies: Array.isArray(savedCompanies) && savedCompanies.length > 0
                ? [0, 1, 2].map(i => ({ ...emptyMotorCompany(), ...(savedCompanies[i] || {}) }))
                : emptyMotorCompanies()
            };
          });
          setProductMotorSettings(motorSettings);
        }
      } catch (err) {
        console.error('Settings fetch error:', err);
      }
    };
    fetchSettings();

    // Fetch Exchange Rates
    const fetchRates = async () => {
      try {
        const res = await fetch('https://open.er-api.com/v6/latest/USD');
        const data = await res.json();
        if (data && data.rates && data.rates.TRY && data.rates.EUR) {
          const usdToTry = data.rates.TRY;
          const eurToTry = data.rates.TRY / data.rates.EUR;
          setExchangeRates({ USD: usdToTry.toFixed(2), EUR: eurToTry.toFixed(2) });
        }
      } catch (err) {
        console.error('Exchange rates error:', err);
      }
    };
    fetchRates();
  }, []);

  const handleSaveSettings = async (overrideData = null) => {
    setIsSavingSettings(true);
    try {
      const settingsRef = doc(db, 'settings', 'global');
      let updateData = overrideData || {};

      if (!overrideData) {
        if (activeTypeTab === 'tul') {
          updateData = {
            tulStandartDikimPrice: Number(tulExtraPrices.standartDikim),
            tulSPilePrice: Number(tulExtraPrices.sPile),
            tulAmerican2Price: Number(tulExtraPrices.american2),
            tulAmerican3Price: Number(tulExtraPrices.american3),
            tulKapsulPrice: Number(tulExtraPrices.kapsul),
            tulAltEtekKatlamaPrice: Number(tulExtraPrices.altEtekKatlama)
          };
        } else if (activeTypeTab === 'kanat') {
          updateData = {
            kanatStandartDikimPrice: Number(kanatExtraPrices.standartDikim),
            kanatSPilePrice: Number(kanatExtraPrices.sPile),
            kanatAmerican2Price: Number(kanatExtraPrices.american2),
            kanatAmerican3Price: Number(kanatExtraPrices.american3),
            kanatKapsulPrice: Number(kanatExtraPrices.kapsul)
          };
        } else if (activeTypeTab === 'ahsapJaluzi') {
          updateData = {
            motorCurrency: motorCurrency,
            zigilPrice: Number(jaluziExtras.zigil),
            zigilCost: Number(jaluziExtras.zigilCost)
          };
        } else if (activeTypeTab === 'metalJaluzi') {
          updateData = {
            motorCurrency: motorCurrency,
            metalZigilPrice: Number(metalJaluziExtras.zigil),
            metalZigilCost: Number(metalJaluziExtras.zigilCost),
            metalReduktorPrice: Number(metalJaluziExtras.reduktor),
            metalReduktorCost: Number(metalJaluziExtras.reduktorCost),
            ikiCamArasiPrice: Number(metalJaluziExtras.ikiCamArasi),
            ikiCamArasiCost: Number(metalJaluziExtras.ikiCamArasiCost)
          };
        } else if (activeTypeTab === 'stor' || activeTypeTab === 'zebra') {
          updateData = buildExtraCompaniesUpdate(activeTypeTab);
        } else if (activeTypeTab === 'dikeyPerde') {
          updateData = {
            dikeyFonPrice: Number(dikeyExtras.fon),
            dikeyFonCost: Number(dikeyExtras.fonCost),
            dikeyFonList: dikeyExtras.fonList || []
          };
        } else if (activeTypeTab === 'katlamaliPerde') {
          updateData = {
            katlamaliMekanizmaList: katlamaliExtras.mekanizmaList || []
          };
        }

        // Motor ayarlarını her zaman dahil et (aktif sekmeye göre) — her tip için en fazla 3 firma
        const motorTypes = ['stor', 'zebra', 'ahsapJaluzi', 'metalJaluzi', 'dikeyPerde', 'dikeyPvc', 'dikeyStor', 'katlamaliPerde'];
        if (motorTypes.includes(activeTypeTab)) {
          const settings = productMotorSettings[activeTypeTab];
          updateData[`${activeTypeTab}MotorCompanies`] = settings.companies.map(c => ({
            name: (c.name || '').trim(),
            motorPrice: Number(c.motorPrice) || 0,
            motorCost: Number(c.motorCost) || 0,
            kumandaPrice: Number(c.kumandaPrice) || 0,
            kumandaCost: Number(c.kumandaCost) || 0,
            currency: c.currency || 'USD',
            rate: Number(c.rate) || 0
          }));
        }
      }

      await setDoc(settingsRef, updateData, { merge: true });
      if (!overrideData) {
        setExcelModal({ show: true, type: 'success', title: 'Başarılı', message: 'Ayarlar başarıyla kaydedildi.' });
      }
    } catch (err) {
      console.error('Settings save error:', err);
      if (!overrideData) {
        setExcelModal({ show: true, type: 'error', title: 'Hata', message: 'Ayarlar kaydedilirken bir hata oluştu.' });
      }
    }
    setIsSavingSettings(false);
  };

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    companyName: '',
    productType: Object.keys(PRODUCT_TYPES)[0],
    currency: 'TL',
    unitPrice: '',
    cost: '',
    // Jaluziler için ekstra fiyatlandırma
    merdivenIpliFiyat: '',
    merdivenIpliMaliyet: '',
    kurdeleliFiyat: '',
    kurdeleliMaliyet: '',
    merdivenIpliReduktorluFiyat: '',
    merdivenIpliReduktorluMaliyet: '',
    kurdelaliReduktorluFiyat: '',
    kurdelaliReduktorluMaliyet: ''
  });
  const [formError, setFormError] = useState(null);
  const [formSuccess, setFormSuccess] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Silme modal
  const [deleteModal, setDeleteModal] = useState(null);
  const [excelModal, setExcelModal] = useState({ show: false, type: '', title: '', message: '', onConfirm: null });

  // Ürünler cihazdaki katalogdan gelir; değişiklikler (bu cihaz veya başka cihaz) aboneliğe otomatik düşer
  useEffect(() => {
    setIsLoading(true);
    return subscribeProductItems((items) => {
      // İsme göre sırala
      setProducts([...items].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'tr')));
      setIsLoading(false);
    }, (error) => {
      console.error('Ürünler yüklenirken hata:', error);
      setIsLoading(false);
    });
  }, []);

  // Sekme değişince arama kutusunu sıfırla
  const handleTabChange = (key) => {
    setActiveTypeTab(key);
    setSearchQuery('');
  };

  // Aktif sekmedeki ürünler (arama filtreli)
  const filteredProducts = products.filter(p => {
    if (p.productType !== activeTypeTab) return false;
    if (!searchQuery.trim()) return true;
    return normalizeSearchText(p.name).includes(normalizeSearchText(searchQuery));
  });

  // Döviz birimi sembolü (Dolar/Euro için)
  const getCurrencySymbol = (cur) => (cur === 'USD' ? '$' : cur === 'EUR' ? '€' : '₺');

  // Bir tutarı, ürünün kendi para biriminden güncel kurla TL'ye çevirir
  const convertToTRY = (amount, cur) => {
    const num = Number(amount) || 0;
    if (!cur || cur === 'TL') return num;
    const rate = Number(exchangeRates[cur]) || 1;
    return num * rate;
  };

  // Tablo/kart görünümünde bir fiyat rozetini, dövizliyse TL karşılığıyla birlikte gösterir
  const renderAmountBadge = (value, cur, badgeClass, fontSize) => {
    const num = Number(value) || 0;
    if (num <= 0) {
      return <span className="no-cost" style={fontSize ? { fontSize } : undefined}>—</span>;
    }
    const isForeign = cur && cur !== 'TL';
    return (
      <span className={badgeClass} style={fontSize ? { fontSize } : undefined}>
        {isForeign
          ? `${getCurrencySymbol(cur)}${formatPrice(num)}`
          : `${formatPrice(num)} ₺`}
        {isForeign && (
          <small className="try-equivalent"> (≈{formatPrice(convertToTRY(num, cur))} ₺)</small>
        )}
      </span>
    );
  };

  // Mobil kart görünümü için etiketli fiyat rozeti ("Maliyet: ...", "Satış: ..." gibi)
  const renderLabeledAmountBadge = (label, value, cur, badgeClass) => {
    const num = Number(value) || 0;
    if (num <= 0) return null;
    const isForeign = cur && cur !== 'TL';
    return (
      <span className={badgeClass}>
        {label}: {isForeign ? `${getCurrencySymbol(cur)}${formatPrice(num)}` : `${formatPrice(num)} ₺`}
        {isForeign && (
          <small className="try-equivalent"> (≈{formatPrice(convertToTRY(num, cur))} ₺)</small>
        )}
      </span>
    );
  };

  // Form aç - yeni ürün
  const handleAddNew = () => {
    setEditingProduct(null);
    setFormData({
      name: '',
      companyName: '',
      productType: activeTypeTab,
      currency: 'TL',
      unitPrice: '',
      cost: '',
      merdivenIpliFiyat: '',
      merdivenIpliMaliyet: '',
      kurdeleliFiyat: '',
      kurdeleliMaliyet: '',
      merdivenIpliReduktorluFiyat: '',
      merdivenIpliReduktorluMaliyet: '',
      kurdelaliReduktorluFiyat: '',
      kurdelaliReduktorluMaliyet: ''
    });
    setFormError(null);
    setFormSuccess(null);
    setShowForm(true);
  };

  // Form aç - düzenle
  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name || '',
      companyName: product.companyName || '',
      productType: product.productType || activeTypeTab,
      currency: product.currency || 'TL',
      unitPrice: product.unitPrice || '',
      cost: product.cost || '',
      merdivenIpliFiyat: product.merdivenIpliFiyat || '',
      merdivenIpliMaliyet: product.merdivenIpliMaliyet || '',
      kurdeleliFiyat: product.kurdeleliFiyat || '',
      kurdeleliMaliyet: product.kurdeleliMaliyet || '',
      merdivenIpliReduktorluFiyat: product.merdivenIpliReduktorluFiyat || '',
      merdivenIpliReduktorluMaliyet: product.merdivenIpliReduktorluMaliyet || '',
      kurdelaliReduktorluFiyat: product.kurdelaliReduktorluFiyat || '',
      kurdelaliReduktorluMaliyet: product.kurdelaliReduktorluMaliyet || ''
    });
    setFormError(null);
    setFormSuccess(null);
    setShowForm(true);
  };

  // Form kapat
  const handleCancel = () => {
    setShowForm(false);
    setEditingProduct(null);
    setFormError(null);
  };

  // Kaydet
  const handleSave = async (e) => {
    e.preventDefault();
    setFormError(null);
    setFormSuccess(null);

    if (!formData.name.trim()) {
      setFormError('Ürün adı zorunludur.');
      return;
    }

    const isJaluzi = formData.productType === 'ahsapJaluzi';
    const isRustik = formData.productType === 'rustik';

    if (!isJaluzi && !isRustik && (!formData.unitPrice || isNaN(parseFloat(formData.unitPrice)))) {
      setFormError('Birim fiyat geçerli bir sayı olmalıdır.');
      return;
    }

    // Jaluziler için en az bir baz fiyat gerekli mi diye kontrol edilebilir, opsiyonel bırakıyoruz/esnek.

    setIsSaving(true);
    try {
      let payload = {
        name: formData.name.trim(),
        companyName: formData.companyName.trim(),
        productType: formData.productType,
        updatedAt: new Date()
      };

      if (isJaluzi) {
        payload = {
          ...payload,
          currency: formData.currency || 'TL',
          merdivenIpliFiyat: formData.merdivenIpliFiyat ? parseFloat(formData.merdivenIpliFiyat) : 0,
          merdivenIpliMaliyet: formData.merdivenIpliMaliyet ? parseFloat(formData.merdivenIpliMaliyet) : 0,
          kurdeleliFiyat: formData.kurdeleliFiyat ? parseFloat(formData.kurdeleliFiyat) : 0,
          kurdeleliMaliyet: formData.kurdeleliMaliyet ? parseFloat(formData.kurdeleliMaliyet) : 0,
          merdivenIpliReduktorluFiyat: formData.merdivenIpliReduktorluFiyat ? parseFloat(formData.merdivenIpliReduktorluFiyat) : 0,
          merdivenIpliReduktorluMaliyet: formData.merdivenIpliReduktorluMaliyet ? parseFloat(formData.merdivenIpliReduktorluMaliyet) : 0,
          kurdelaliReduktorluFiyat: formData.kurdelaliReduktorluFiyat ? parseFloat(formData.kurdelaliReduktorluFiyat) : 0,
          kurdelaliReduktorluMaliyet: formData.kurdelaliReduktorluMaliyet ? parseFloat(formData.kurdelaliReduktorluMaliyet) : 0
        };
      } else if (isRustik) {
        payload = {
          ...payload,
          currency: 'TL',
          unitPrice: 0,
          cost: 0
        };
      } else {
        payload = {
          ...payload,
          currency: formData.currency || 'TL',
          unitPrice: parseFloat(formData.unitPrice),
          cost: formData.cost ? parseFloat(formData.cost) : 0
        };
      }

      if (editingProduct) {
        await updateDoc(doc(db, 'productItems', editingProduct.id), { ...payload, ...productWriteFields() });
        setFormSuccess('Ürün başarıyla güncellendi!');
      } else {
        await addDoc(collection(db, 'productItems'), {
          ...payload,
          createdAt: new Date(),
          ...productWriteFields()
        });
        setFormSuccess('Ürün başarıyla eklendi!');
      }
      // Yeni ürün eklenince o tipin sekmesine geç
      setActiveTypeTab(formData.productType);

      setTimeout(() => {
        setShowForm(false);
        setEditingProduct(null);
        setFormSuccess(null);
      }, 1200);
    } catch (error) {
      console.error('Ürün kaydedilirken hata:', error);
      setFormError('Ürün kaydedilirken bir hata oluştu: ' + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  // Silme
  const handleDelete = async () => {
    if (!deleteModal) return;
    try {
      // Silindi işareti: diğer cihazlardaki katalog da bu ürünü düşürsün
      await updateDoc(doc(db, 'productItems', deleteModal.id), productDeleteFields());
      setDeleteModal(null);
    } catch (error) {
      console.error('Ürün silinirken hata:', error);
    }
  };

  // Excel'e Aktar
  const handleExportExcel = () => {
    if (filteredProducts.length === 0) {
      setExcelModal({ show: true, type: 'info', title: 'Bilgi', message: 'Dışa aktarılacak ürün bulunamadı.' });
      return;
    }

    const isJaluzi = activeTypeTab === 'ahsapJaluzi';

    const exportData = filteredProducts.map(p => {
      if (isJaluzi) {
        return {
          'Ürün Adı': p.name,
          'Firma Adı': p.companyName || '',
          'Para Birimi': p.currency || 'TL',
          'Merdiven İpli Maliyet': p.merdivenIpliMaliyet || 0,
          'Merdiven İpli': p.merdivenIpliFiyat || 0,
          'Kurdeleli Maliyet': p.kurdeleliMaliyet || 0,
          'Kurdeleli': p.kurdeleliFiyat || 0,
          'Merdiven İpli Redüktörlü Maliyet': p.merdivenIpliReduktorluMaliyet || 0,
          'Merdiven İpli Redüktörlü': p.merdivenIpliReduktorluFiyat || 0,
          'Kurdelalı Redüktörlü Maliyet': p.kurdelaliReduktorluMaliyet || 0,
          'Kurdelalı Redüktörlü': p.kurdelaliReduktorluFiyat || 0
        };
      } else {
        return {
          'Ürün Adı': p.name,
          'Firma Adı': p.companyName || '',
          'Para Birimi': p.currency || 'TL',
          'Maliyet': p.cost || 0,
          'Birim Fiyat': p.unitPrice
        };
      }
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Ürünler');

    const typeLabel = PRODUCT_TYPES[activeTypeTab]?.label || 'Urunler';
    XLSX.writeFile(workbook, `${typeLabel}_Urun_Listesi.xlsx`);
  };

  // Excel'den Yükle butonu tıklaması
  const handleImportClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Listeyi Temizle
  const handleClearList = () => {
    const typeLabel = PRODUCT_TYPES[activeTypeTab]?.label;
    const currentCount = products.filter(p => p.productType === activeTypeTab).length;
    if (currentCount === 0) {
      setExcelModal({ show: true, type: 'info', title: 'Bilgi', message: 'Bu kategoride silinecek ürün bulunamadı.' });
      return;
    }
    setExcelModal({
      show: true,
      type: 'confirm',
      title: 'Listeyi Temizle',
      message: `DİKKAT! "${typeLabel}" kategorisindeki ${currentCount} ürünün TAMAMI silinecektir.\n\nBu işlem geri alınamaz. Emin misiniz?`,
      onConfirm: async () => {
        setExcelModal({ show: false });
        setIsExcelLoading(true);
        try {
          const toDelete = products.filter(p => p.productType === activeTypeTab);
          await commitInChunks(toDelete, (batch, p) => batch.update(doc(db, 'productItems', p.id), productDeleteFields()));
          setExcelModal({ show: true, type: 'success', title: 'Temizlendi', message: `${toDelete.length} ürün başarıyla silindi.` });
        } catch (err) {
          setExcelModal({ show: true, type: 'error', title: 'Hata', message: 'Liste temizlenirken bir hata oluştu: ' + err.message });
        } finally {
          setIsExcelLoading(false);
        }
      }
    });
  };

  // Excel'den Dataları Veritabanına Yükle — Upsert mantığı (ada göre eşleştir)
  const processExcelData = async (data, typeLabel) => {
    setExcelModal({ show: false });
    try {
      const isJaluzi = activeTypeTab === 'ahsapJaluzi';

      // Mevcut ürünleri normalize edilmiş ada göre map'le
      const existingByName = {};
      products
        .filter(p => p.productType === activeTypeTab)
        .forEach(p => {
          existingByName[(p.name || '').toLowerCase().trim()] = p;
        });

      const writes = []; // [{ ref, data, isNew }]
      let updateCount = 0;
      let addCount = 0;

      for (const row of data) {
        const name = row['Ürün Adı']?.toString().trim();
        if (!name) continue;

        const rawCurrency = row['Para Birimi']?.toString().trim().toUpperCase();
        const currency = ['USD', 'EUR'].includes(rawCurrency) ? rawCurrency : 'TL';
        const companyName = row['Firma Adı']?.toString().trim() || '';

        let payload = {
          name,
          companyName,
          productType: activeTypeTab
        };

        let isValid = false;

        if (isJaluzi) {
          payload = {
            ...payload,
            currency,
            merdivenIpliFiyat: parseFloat(row['Merdiven İpli']) || 0,
            merdivenIpliMaliyet: parseFloat(row['Merdiven İpli Maliyet']) || 0,
            kurdeleliFiyat: parseFloat(row['Kurdeleli']) || 0,
            kurdeleliMaliyet: parseFloat(row['Kurdeleli Maliyet']) || 0,
            merdivenIpliReduktorluFiyat: parseFloat(row['Merdiven İpli Redüktörlü']) || 0,
            merdivenIpliReduktorluMaliyet: parseFloat(row['Merdiven İpli Redüktörlü Maliyet']) || 0,
            kurdelaliReduktorluFiyat: parseFloat(row['Kurdelalı Redüktörlü']) || 0,
            kurdelaliReduktorluMaliyet: parseFloat(row['Kurdelalı Redüktörlü Maliyet']) || 0
          };
          isValid = true;
        } else {
          const unitPrice = parseFloat(row['Birim Fiyat']);
          const cost = parseFloat(row['Maliyet']);
          if (!isNaN(unitPrice)) {
            payload = { ...payload, currency, unitPrice, cost: isNaN(cost) ? 0 : cost };
            isValid = true;
          }
        }

        if (!isValid) continue;

        const existing = existingByName[name.toLowerCase()];
        if (existing) {
          // Değişmeyen ürünü yazma: her yazma ücretli ve tüm cihazlara yeniden indirilir
          if (Object.keys(payload).every(key => existing[key] === payload[key])) continue;
          writes.push({ ref: doc(db, 'productItems', existing.id), data: { ...payload, ...productWriteFields() } });
          updateCount++;
        } else {
          // Yeni ürün ekle
          writes.push({ ref: doc(collection(db, 'productItems')), data: { ...payload, createdAt: new Date(), ...productWriteFields() }, isNew: true });
          addCount++;
        }
      }

      await commitInChunks(writes, (batch, w) => (w.isNew ? batch.set(w.ref, w.data) : batch.update(w.ref, w.data)));

      const msg = [
        updateCount > 0 ? `${updateCount} ürün güncellendi` : null,
        addCount > 0 ? `${addCount} yeni ürün eklendi` : null
      ].filter(Boolean).join(', ');

      setExcelModal({ show: true, type: 'success', title: 'Başarılı', message: msg ? `${msg}.` : 'Değişiklik yapılmadı.' });
    } catch (error) {
      console.error('Excel içe aktarılırken hata:', error);
      setExcelModal({ show: true, type: 'error', title: 'Hata', message: 'Excel dosyası okunurken bir hata oluştu: ' + error.message });
    } finally {
      setIsExcelLoading(false);
    }
  };

  // Excel Dosya Seçimi ve İşlenmesi
  const handleImportExcel = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // input değerini sıfırlayalım ki aynı dosyayı bir daha seçebilsin
    e.target.value = null;

    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        setIsExcelLoading(true);
        const bstr = evt.target.result;
        const workbook = XLSX.read(bstr, { type: 'binary' });
        const worksheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[worksheetName];
        const data = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

        if (data.length === 0) {
          setExcelModal({ show: true, type: 'error', title: 'Hata', message: 'Excel dosyası boş veya okunamadı.' });
          setIsExcelLoading(false);
          return;
        }

        // Başlık kontrolü
        const firstRow = data[0];
        const isJaluzi = activeTypeTab === 'ahsapJaluzi';

        if (isJaluzi) {
          if (!('Ürün Adı' in firstRow) || !('Merdiven İpli' in firstRow)) {
            setExcelModal({ show: true, type: 'error', title: 'Hatalı Format', message: `Hatalı Excel formatı! Ahşap/Metal Jaluzi dosyalarında "Ürün Adı", "Merdiven İpli", "Kurdeleli" gibi başlıklar bulunmalıdır.\n\n(Lütfen taslağı görmek için önce "Excel'e Aktar" diyerek örnek dosyayı indirin)` });
            setIsExcelLoading(false);
            return;
          }
        } else {
          if (!('Ürün Adı' in firstRow) || !('Birim Fiyat' in firstRow)) {
            setExcelModal({ show: true, type: 'error', title: 'Hatalı Format', message: `Hatalı Excel formatı! Dosyada "Ürün Adı" ve "Birim Fiyat" başlıkları bulunmalıdır.\n\n(Lütfen taslağı görmek için önce "Excel'e Aktar" diyerek örnek dosyayı indirin)` });
            setIsExcelLoading(false);
            return;
          }
        }

        // Upsert onayı — ID'ler korunur, yalnızca güncelleme/ekleme yapılır
        const typeLabel = PRODUCT_TYPES[activeTypeTab]?.label;
        const existingCount = products.filter(p => p.productType === activeTypeTab).length;
        setExcelModal({
          show: true,
          type: 'confirm',
          title: 'Excel Yükleme Onayı',
          message: `Excel dosyasındaki ${data.length} ürün işlenecek:\n\n• Mevcut ${existingCount} ürün içinden eşleşenler güncellenir\n• Listede olmayan yeni ürünler eklenir\n• Listede olup Excel'de olmayan ürünlere dokunulmaz\n\nOnaylıyor musunuz?`,
          onConfirm: () => processExcelData(data, typeLabel)
        });

        // Modal açıkken arkada loading dönmesini kapatmak veya açık bırakmak size kalmış
        // Biz processExcelData içerisinde tekrar true/false yapacağız.
      } catch (error) {
        console.error('Excel dosyası parse edilirken hata:', error);
        setExcelModal({ show: true, type: 'error', title: 'Parse Hatası', message: 'Dosya okunurken bir hata oluştu.' });
        setIsExcelLoading(false);
      }
    };
    reader.readAsBinaryString(file);
  };

  return (
    <div className="product-list-page">
      <div className="product-list-header">
        <div className="product-list-title">
          <h2>Ürün Listesi</h2>
          <p>Ürün tipine göre kategorize edilmiş ürün kataloğu</p>
        </div>

        <div className="modern-rates-container">
          <div className="rates-badges">
            <span className="rate-badge usd" title="Güncel Dolar Kuru">
              <i className="fas fa-dollar-sign"></i>
              <span>USD: <b>{exchangeRates.USD}</b> ₺</span>
            </span>
            <span className="rate-badge eur" title="Güncel Euro Kuru">
              <i className="fas fa-euro-sign"></i>
              <span>EUR: <b>{exchangeRates.EUR}</b> ₺</span>
            </span>
          </div>
        </div>
      </div>


      {/* Ürün Tipi Navigasyon */}
      <div className="product-nav-container">
        {Object.entries(PRODUCT_TYPES)
          .filter(([key]) => {
            if (isMontajci) return key === 'plise';
            return true;
          })
          .map(([key, type]) => {
            const count = products.filter(p => p.productType === key).length;
            return (
              <button
                key={key}
                className={`nav-pill ${activeTypeTab === key ? 'active' : ''}`}
                onClick={() => handleTabChange(key)}
              >
                <i className={`fas fa-${
                  key === 'tul' ? 'scroll' : 
                  key === 'kanat' ? 'border-all' : 
                  key === 'ahsapJaluzi' ? 'align-justify' : 
                  key === 'metalJaluzi' ? 'th-list' : 
                  key === 'stor' ? 'window-maximize' : 
                  key === 'zebra' ? 'equals' : 
                  key === 'dikeyPerde' ? 'columns' : 
                  key === 'plise' ? 'layer-group' : 
                  key === 'raySistemi' ? 'road' : 
                  key === 'rustik' ? 'ellipsis-h' : 'box'
                }`}></i>
                {type.label}
                <span className={`tab-count ${isLoading ? 'loading' : ''} ${!isLoading && count === 0 ? 'empty' : ''}`}>
                  {isLoading ? '...' : count}
                </span>
              </button>
            );
          })}
      </div>

      <div className="product-table-wrapper">
        <div className="product-table-header-bar">
          <div className="product-table-title">
            <i className="fas fa-list"></i>
            <span>{PRODUCT_TYPES[activeTypeTab]?.label} Ürünleri</span>
            <span className="product-count">({filteredProducts.length} ürün)</span>
          </div>
          {(isAdmin || isSatisci) && !isMontajci && (
            <div className="excel-actions">
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                accept=".xlsx, .xls, .csv"
                onChange={handleImportExcel}
              />
              <button
                className="btn-excel btn-import"
                onClick={handleImportClick}
                disabled={isExcelLoading}
              >
                {isExcelLoading ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-file-import"></i>} Excel'den Yükle
              </button>
              <button
                className="btn-excel btn-export"
                onClick={handleExportExcel}
                disabled={filteredProducts.length === 0 || isExcelLoading}
              >
                <i className="fas fa-file-export"></i> Excel'e Aktar
              </button>
              <button
                className="btn-excel btn-clear-list"
                onClick={handleClearList}
                disabled={isExcelLoading}
                title="Bu kategorideki tüm ürünleri sil"
              >
                <i className="fas fa-trash-alt"></i> Listeyi Temizle
              </button>
            </div>
          )}
        </div>

        {/* Sekmeye özel ayarlar (Ürün Listesi Başlığı Altında) */}
        <div className="product-settings-container">
          {/* Rustik Ayarları */}
          {activeTypeTab === 'rustik' && (isAdmin || isSatisci) && (
            <div className="settings-card">
              <div 
                className="settings-header clickable" 
                onClick={() => setIsRustikSettingsCollapsed(!isRustikSettingsCollapsed)}
              >
                <div className="settings-title">
                  <i className="fas fa-ellipsis-h"></i>
                  <span>Rustik Ayak Ücret Ayarları</span>
                </div>
                <i className={`fas fa-chevron-${isRustikSettingsCollapsed ? 'down' : 'up'}`}></i>
              </div>
              
              {!isRustikSettingsCollapsed && (
                <div className="settings-content">
                  <div className="settings-grid">
                    {/* Ayak Modeli Listesi */}
                    <div className="settings-column">
                      <div 
                        className="settings-sub-header clickable"
                        onClick={() => setIsRustikModelCollapsed(!isRustikModelCollapsed)}
                      >
                        <h4><i className="fas fa-couch"></i> Ayak Model Listesi</h4>
                        <i className={`fas fa-chevron-${isRustikModelCollapsed ? 'down' : 'up'}`}></i>
                      </div>
                      
                      {!isRustikModelCollapsed && (
                        <div className="settings-sub-content">
                          <div className="list-add-form">
                            <input 
                              type="text" 
                              placeholder="Yeni Model" 
                              value={newRustikModel}
                              onChange={(e) => setNewRustikModel(e.target.value)}
                            />
                            <button onClick={() => {
                              if (!newRustikModel) return;
                              const newList = [...rustikExtras.ayakModelList, newRustikModel];
                              setRustikExtras({...rustikExtras, ayakModelList: newList});
                              setNewRustikModel('');
                              handleSaveSettings({ rustikAyakModelList: newList });
                            }}><i className="fas fa-plus"></i></button>
                          </div>
                          <div className="settings-list-container">
                            {rustikExtras.ayakModelList.length === 0 && <span className="empty-list-msg">Liste boş</span>}
                            <ul className="settings-inner-list">
                              {rustikExtras.ayakModelList.map((item, index) => (
                                <li key={index} className="settings-list-item">
                                  {editingRustikModel.index === index ? (
                                    <div className="edit-mode-container">
                                      <input 
                                        type="text" 
                                        value={editingRustikModel.value}
                                        onChange={(e) => setEditingRustikModel({...editingRustikModel, value: e.target.value})}
                                        autoFocus
                                      />
                                      <div className="edit-actions">
                                        <button className="btn-confirm" onClick={() => {
                                          const newList = [...rustikExtras.ayakModelList];
                                          newList[index] = editingRustikModel.value;
                                          setRustikExtras({...rustikExtras, ayakModelList: newList});
                                          handleSaveSettings({ rustikAyakModelList: newList });
                                          setEditingRustikModel({ index: null, value: '' });
                                        }}><i className="fas fa-check"></i></button>
                                        <button className="btn-cancel" onClick={() => setEditingRustikModel({ index: null, value: '' })}><i className="fas fa-times"></i></button>
                                      </div>
                                    </div>
                                  ) : (
                                    <>
                                      <span className="item-text">{item}</span>
                                      <div className="item-actions">
                                        <button className="btn-edit" onClick={() => setEditingRustikModel({ index, value: item })}><i className="fas fa-edit"></i></button>
                                        <button className="btn-delete" onClick={() => {
                                          const newList = rustikExtras.ayakModelList.filter((_, i) => i !== index);
                                          setRustikExtras({...rustikExtras, ayakModelList: newList});
                                          handleSaveSettings({ rustikAyakModelList: newList });
                                        }}><i className="fas fa-trash-alt"></i></button>
                                      </div>
                                    </>
                                  )}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Ayak Çap Listesi */}
                    <div className="settings-column">
                      <div 
                        className="settings-sub-header clickable"
                        onClick={() => setIsRustikCapCollapsed(!isRustikCapCollapsed)}
                      >
                        <h4><i className="fas fa-ruler-combined"></i> Ayak Çap Listesi</h4>
                        <i className={`fas fa-chevron-${isRustikCapCollapsed ? 'down' : 'up'}`}></i>
                      </div>

                      {!isRustikCapCollapsed && (
                        <div className="settings-sub-content">
                          <div className="list-add-form">
                            <input 
                              type="text" 
                              placeholder="Yeni Çap" 
                              value={newRustikCap}
                              onChange={(e) => setNewRustikCap(e.target.value)}
                            />
                            <button onClick={() => {
                              if (!newRustikCap) return;
                              const newList = [...rustikExtras.ayakCapList, newRustikCap];
                              setRustikExtras({...rustikExtras, ayakCapList: newList});
                              setNewRustikCap('');
                              handleSaveSettings({ rustikAyakCapList: newList });
                            }}><i className="fas fa-plus"></i></button>
                          </div>
                          <div className="settings-list-container">
                            {rustikExtras.ayakCapList.length === 0 && <span className="empty-list-msg">Liste boş</span>}
                            <ul className="settings-inner-list">
                              {rustikExtras.ayakCapList.map((item, index) => (
                                <li key={index} className="settings-list-item">
                                  {editingRustikCap.index === index ? (
                                    <div className="edit-mode-container">
                                      <input 
                                        type="text" 
                                        value={editingRustikCap.value}
                                        onChange={(e) => setEditingRustikCap({...editingRustikCap, value: e.target.value})}
                                        autoFocus
                                      />
                                      <div className="edit-actions">
                                        <button className="btn-confirm" onClick={() => {
                                          const newList = [...rustikExtras.ayakCapList];
                                          newList[index] = editingRustikCap.value;
                                          setRustikExtras({...rustikExtras, ayakCapList: newList});
                                          handleSaveSettings({ rustikAyakCapList: newList });
                                          setEditingRustikCap({ index: null, value: '' });
                                        }}><i className="fas fa-check"></i></button>
                                        <button className="btn-cancel" onClick={() => setEditingRustikCap({ index: null, value: '' })}><i className="fas fa-times"></i></button>
                                      </div>
                                    </div>
                                  ) : (
                                    <>
                                      <span className="item-text">{item}</span>
                                      <div className="item-actions">
                                        <button className="btn-edit" onClick={() => setEditingRustikCap({ index, value: item })}><i className="fas fa-edit"></i></button>
                                        <button className="btn-delete" onClick={() => {
                                          const newList = rustikExtras.ayakCapList.filter((_, i) => i !== index);
                                          setRustikExtras({...rustikExtras, ayakCapList: newList});
                                          handleSaveSettings({ rustikAyakCapList: newList });
                                        }}><i className="fas fa-trash-alt"></i></button>
                                      </div>
                                    </>
                                  )}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Ayak Uzunluk Listesi */}
                    <div className="settings-column">
                      <div 
                        className="settings-sub-header clickable"
                        onClick={() => setIsRustikUzunlukCollapsed(!isRustikUzunlukCollapsed)}
                      >
                        <h4><i className="fas fa-arrows-alt-v"></i> Ayak Uzunluğu</h4>
                        <i className={`fas fa-chevron-${isRustikUzunlukCollapsed ? 'down' : 'up'}`}></i>
                      </div>

                      {!isRustikUzunlukCollapsed && (
                        <div className="settings-sub-content">
                          <div className="list-add-form">
                            <input 
                              type="text" 
                              placeholder="Yeni Uzunluk" 
                              value={newRustikUzunluk}
                              onChange={(e) => setNewRustikUzunluk(e.target.value)}
                            />
                            <button onClick={() => {
                              if (!newRustikUzunluk) return;
                              const newList = [...rustikExtras.ayakUzunlukList, newRustikUzunluk];
                              setRustikExtras({...rustikExtras, ayakUzunlukList: newList});
                              setNewRustikUzunluk('');
                              handleSaveSettings({ rustikAyakUzunlukList: newList });
                            }}><i className="fas fa-plus"></i></button>
                          </div>
                          <div className="settings-list-container">
                            {rustikExtras.ayakUzunlukList.length === 0 && <span className="empty-list-msg">Liste boş</span>}
                            <ul className="settings-inner-list">
                              {rustikExtras.ayakUzunlukList.map((item, index) => (
                                <li key={index} className="settings-list-item">
                                  {editingRustikUzunluk.index === index ? (
                                    <div className="edit-mode-container">
                                      <input 
                                        type="text" 
                                        value={editingRustikUzunluk.value}
                                        onChange={(e) => setEditingRustikUzunluk({...editingRustikUzunluk, value: e.target.value})}
                                        autoFocus
                                      />
                                      <div className="edit-actions">
                                        <button className="btn-confirm" onClick={() => {
                                          const newList = [...rustikExtras.ayakUzunlukList];
                                          newList[index] = editingRustikUzunluk.value;
                                          setRustikExtras({...rustikExtras, ayakUzunlukList: newList});
                                          handleSaveSettings({ rustikAyakUzunlukList: newList });
                                          setEditingRustikUzunluk({ index: null, value: '' });
                                        }}><i className="fas fa-check"></i></button>
                                        <button className="btn-cancel" onClick={() => setEditingRustikUzunluk({ index: null, value: '' })}><i className="fas fa-times"></i></button>
                                      </div>
                                    </div>
                                  ) : (
                                    <>
                                      <span className="item-text">{item}</span>
                                      <div className="item-actions">
                                        <button className="btn-edit" onClick={() => setEditingRustikUzunluk({ index, value: item })}><i className="fas fa-edit"></i></button>
                                        <button className="btn-delete" onClick={() => {
                                          const newList = rustikExtras.ayakUzunlukList.filter((_, i) => i !== index);
                                          setRustikExtras({...rustikExtras, ayakUzunlukList: newList});
                                          handleSaveSettings({ rustikAyakUzunlukList: newList });
                                        }}><i className="fas fa-trash-alt"></i></button>
                                      </div>
                                    </>
                                  )}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Ayak Renk Listesi */}
                    <div className="settings-column">
                      <div 
                        className="settings-sub-header clickable"
                        onClick={() => setIsRustikRenkCollapsed(!isRustikRenkCollapsed)}
                      >
                        <h4><i className="fas fa-palette"></i> Renk Kodu Listesi</h4>
                        <i className={`fas fa-chevron-${isRustikRenkCollapsed ? 'down' : 'up'}`}></i>
                      </div>

                      {!isRustikRenkCollapsed && (
                        <div className="settings-sub-content">
                          <div className="list-add-form">
                            <input 
                              type="text" 
                              placeholder="Yeni Renk" 
                              value={newRustikRenk}
                              onChange={(e) => setNewRustikRenk(e.target.value)}
                            />
                            <button onClick={() => {
                              if (!newRustikRenk) return;
                              const newList = [...rustikExtras.ayakRenkKoduList, newRustikRenk];
                              setRustikExtras({...rustikExtras, ayakRenkKoduList: newList});
                              setNewRustikRenk('');
                              handleSaveSettings({ rustikAyakRenkKoduList: newList });
                            }}><i className="fas fa-plus"></i></button>
                          </div>
                          <div className="settings-list-container">
                            {rustikExtras.ayakRenkKoduList.length === 0 && <span className="empty-list-msg">Liste boş</span>}
                            <ul className="settings-inner-list">
                              {rustikExtras.ayakRenkKoduList.map((item, index) => (
                                <li key={index} className="settings-list-item">
                                  {editingRustikRenk.index === index ? (
                                    <div className="edit-mode-container">
                                      <input 
                                        type="text" 
                                        value={editingRustikRenk.value}
                                        onChange={(e) => setEditingRustikRenk({...editingRustikRenk, value: e.target.value})}
                                        autoFocus
                                      />
                                      <div className="edit-actions">
                                        <button className="btn-confirm" onClick={() => {
                                          const newList = [...rustikExtras.ayakRenkKoduList];
                                          newList[index] = editingRustikRenk.value;
                                          setRustikExtras({...rustikExtras, ayakRenkKoduList: newList});
                                          handleSaveSettings({ rustikAyakRenkKoduList: newList });
                                          setEditingRustikRenk({ index: null, value: '' });
                                        }}><i className="fas fa-check"></i></button>
                                        <button className="btn-cancel" onClick={() => setEditingRustikRenk({ index: null, value: '' })}><i className="fas fa-times"></i></button>
                                      </div>
                                    </div>
                                  ) : (
                                    <>
                                      <span className="item-text">{item}</span>
                                      <div className="item-actions">
                                        <button className="btn-edit" onClick={() => setEditingRustikRenk({ index, value: item })}><i className="fas fa-edit"></i></button>
                                        <button className="btn-delete" onClick={() => {
                                          const newList = rustikExtras.ayakRenkKoduList.filter((_, i) => i !== index);
                                          setRustikExtras({...rustikExtras, ayakRenkKoduList: newList});
                                          handleSaveSettings({ rustikAyakRenkKoduList: newList });
                                        }}><i className="fas fa-trash-alt"></i></button>
                                      </div>
                                    </>
                                  )}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div style={{ padding: '0 20px' }}>
          {(activeTypeTab === 'tul' || activeTypeTab === 'kanat') && (
            <div className="settings-card pile-extra-card" style={{ marginBottom: '20px' }}>
              <div className="settings-card-title">
                <i className="fas fa-cut"></i>
                <h4>{activeTypeTab === 'tul' ? 'Tül' : 'Kanat'} Pile Ek Ücret Ayarları (₺)</h4>
              </div>
              <p className="pile-extra-hint">
                Buradaki ücretler müşteriye yansıyan birim fiyattır (metre başı). Atölye dikim ücretleri Giderler &gt; Atölye Giderleri'nden takip edilir.
              </p>
              <div className="settings-grid">
                {PILE_EXTRA_ROWS.filter(row => !row.tulOnly || activeTypeTab === 'tul').map(row => {
                  const prices = activeTypeTab === 'tul' ? tulExtraPrices : kanatExtraPrices;
                  const setPrices = activeTypeTab === 'tul' ? setTulExtraPrices : setKanatExtraPrices;
                  return (
                    <DualPriceCostInput
                      key={row.field}
                      label={row.label}
                      showCost={false}
                      priceValue={prices[row.field]}
                      onPriceChange={(e) => setPrices(p => ({ ...p, [row.field]: e.target.value }))}
                    />
                  );
                })}
              </div>
              <div className="settings-card-actions">
                <button type="button" className="btn-save" onClick={() => handleSaveSettings()} disabled={isSavingSettings}>
                  <i className="fas fa-save"></i> {isSavingSettings ? 'Kaydediliyor...' : 'Ayarları Kaydet'}
                </button>
              </div>
            </div>
          )}

          {['ahsapJaluzi', 'metalJaluzi', 'stor', 'zebra', 'dikeyPerde', 'dikeyPvc', 'dikeyStor', 'katlamaliPerde'].includes(activeTypeTab) && (
            <MotorSettingsCard
              type={activeTypeTab}
              settings={productMotorSettings[activeTypeTab]}
              onCompanyFieldChange={handleMotorCompanyFieldChange}
              onSave={handleSaveSettings}
              isSaving={isSavingSettings}
              exchangeRates={exchangeRates}
              isCollapsed={isMotorSettingsCollapsed}
              onToggleCollapse={() => setIsMotorSettingsCollapsed(!isMotorSettingsCollapsed)}
            />
          )}

          {activeTypeTab === 'ahsapJaluzi' && (
            <div className="settings-card" style={{ marginBottom: '20px' }}>
              <div className="settings-card-title">
                <i className="fas fa-sliders-h"></i>
                <h4>Ahşap Jaluzi Ek Ücretler (₺)</h4>
              </div>
              <div className="settings-grid">
                <DualPriceCostInput
                  label="Zıgıl Sistem"
                  costValue={jaluziExtras.zigilCost}
                  priceValue={jaluziExtras.zigil}
                  onCostChange={(e) => setJaluziExtras(p => ({ ...p, zigilCost: e.target.value }))}
                  onPriceChange={(e) => setJaluziExtras(p => ({ ...p, zigil: e.target.value }))}
                />
              </div>
              <div className="settings-card-actions">
                <button type="button" className="btn-save" onClick={() => handleSaveSettings()} disabled={isSavingSettings}>
                  <i className="fas fa-save"></i> {isSavingSettings ? 'Kaydediliyor...' : 'Ayarları Kaydet'}
                </button>
              </div>
            </div>
          )}

          {activeTypeTab === 'metalJaluzi' && (
            <div className="settings-card" style={{ marginBottom: '20px' }}>
              <div className="settings-card-title">
                <i className="fas fa-layer-group"></i>
                <h4>Metal Jaluzi Ek Ücret Ayarları (₺)</h4>
              </div>
              <div className="settings-grid">
                <DualPriceCostInput
                  label="Zıgıl Sistem (Adet)"
                  costValue={metalJaluziExtras.zigilCost}
                  priceValue={metalJaluziExtras.zigil}
                  onCostChange={(e) => setMetalJaluziExtras(p => ({ ...p, zigilCost: e.target.value }))}
                  onPriceChange={(e) => setMetalJaluziExtras(p => ({ ...p, zigil: e.target.value }))}
                />
                <DualPriceCostInput
                  label="Redüktörlü Farkı (m2)"
                  costValue={metalJaluziExtras.reduktorCost}
                  priceValue={metalJaluziExtras.reduktor}
                  onCostChange={(e) => setMetalJaluziExtras(p => ({ ...p, reduktorCost: e.target.value }))}
                  onPriceChange={(e) => setMetalJaluziExtras(p => ({ ...p, reduktor: e.target.value }))}
                />
                <DualPriceCostInput
                  label="İki Cam Arası (Adet)"
                  costValue={metalJaluziExtras.ikiCamArasiCost}
                  priceValue={metalJaluziExtras.ikiCamArasi}
                  onCostChange={(e) => setMetalJaluziExtras(p => ({ ...p, ikiCamArasiCost: e.target.value }))}
                  onPriceChange={(e) => setMetalJaluziExtras(p => ({ ...p, ikiCamArasi: e.target.value }))}
                />
              </div>
              <div className="settings-card-actions">
                <button type="button" className="btn-save" onClick={() => handleSaveSettings()} disabled={isSavingSettings}>
                  <i className="fas fa-save"></i> {isSavingSettings ? 'Kaydediliyor...' : 'Ayarları Kaydet'}
                </button>
              </div>
            </div>
          )}

          {(activeTypeTab === 'stor' || activeTypeTab === 'zebra') && (
            <StorZebraExtrasCard
              type={activeTypeTab}
              title={activeTypeTab === 'stor' ? 'Stor Perde Ücret Ayarları (₺)' : 'Zebra Perde Ücret Ayarları (₺)'}
              icon={activeTypeTab === 'stor' ? 'fa-cog' : 'fa-stream'}
              companies={extraCompanies[activeTypeTab]}
              activeId={activeExtraCompany[activeTypeTab]}
              onSelect={(type, id) => setActiveExtraCompany(prev => ({ ...prev, [type]: id }))}
              onAdd={handleExtraCompanyAdd}
              onRemove={handleExtraCompanyRemove}
              onRename={handleExtraCompanyRename}
              onPriceChange={handleExtraCompanyPriceChange}
              onSave={handleSaveSettings}
              isSaving={isSavingSettings}
              {...(activeTypeTab === 'stor' ? { isCollapsed: isStorExtrasCollapsed, onToggleCollapse: () => setIsStorExtrasCollapsed(!isStorExtrasCollapsed) } : {})}
            />
          )}

          {activeTypeTab === 'dikeyPerde' && (
            <div className="settings-card" style={{ marginBottom: '20px' }}>
              <div className="settings-card-title">
                <i className="fas fa-arrows-alt-v"></i>
                <h4>Dikey Perde Fon Ayarları (₺)</h4>
              </div>
              <div className="settings-grid">
                <DualPriceCostInput
                  label="Dikey Perde Fon Ücreti (m2)"
                  costValue={dikeyExtras.fonCost}
                  priceValue={dikeyExtras.fon}
                  onCostChange={(e) => setDikeyExtras(p => ({ ...p, fonCost: e.target.value }))}
                  onPriceChange={(e) => setDikeyExtras(p => ({ ...p, fon: e.target.value }))}
                />
              </div>
              <div className="settings-card-actions">
                <button type="button" className="btn-save" onClick={() => handleSaveSettings()} disabled={isSavingSettings}>
                  <i className="fas fa-save"></i> {isSavingSettings ? 'Kaydediliyor...' : 'Ayarları Kaydet'}
                </button>
              </div>
            </div>
          )}

          {activeTypeTab === 'katlamaliPerde' && (
            <div className="settings-card" style={{ marginBottom: '20px' }}>
              <div className="settings-card-title">
                <i className="fas fa-tools"></i>
                <h4>Katlamalı Perde Mekanizma Ayarları</h4>
              </div>
              
              <div style={{ width: '100%' }}>
                <div
                  onClick={() => setIsKatlamaliMekanizmaCollapsed(!isKatlamaliMekanizmaCollapsed)}
                  className="settings-sub-header"
                  style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    cursor: 'pointer', 
                    padding: '12px 16px',
                    background: '#f8fafc',
                    borderRadius: '10px',
                    border: '1px solid #e2e8f0',
                    marginBottom: '15px',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <i className="fas fa-list-ul" style={{ color: '#64748b', fontSize: '14px' }}></i>
                    <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: '#334155' }}>Mekanizma Tipleri ve Ücretleri</h4>
                  </div>
                  <i className={`fas fa-chevron-${isKatlamaliMekanizmaCollapsed ? 'down' : 'up'}`} style={{ color: '#64748b', fontSize: '12px' }}></i>
                </div>

                {!isKatlamaliMekanizmaCollapsed && (
                  <div className="mekanizma-settings-content" style={{ animation: 'slideDown 0.3s ease-out', padding: '0 4px' }}>
                    <div className="add-mekanizma-row" style={{ 
                      display: 'flex', 
                      gap: '12px', 
                      marginBottom: '20px', 
                      padding: '16px',
                      background: '#fff',
                      border: '1px dashed #cbd5e1',
                      borderRadius: '12px',
                      alignItems: 'flex-end',
                      flexWrap: 'wrap'
                    }}>
                      <div className="input-with-small-label" style={{ flex: '2 1 200px' }}>
                        <small>Mekanizma Tipi</small>
                        <input
                          type="text"
                          placeholder="Örn: 5+1 Mekanizma"
                          value={newKatlamaliMekanizma.type}
                          onChange={(e) => setNewKatlamaliMekanizma({ ...newKatlamaliMekanizma, type: e.target.value })}
                          className="modern-input"
                        />
                      </div>
                      <div className="input-with-small-label" style={{ flex: '1 1 120px' }}>
                        <small>Maliyet</small>
                        <div className="input-icon-wrapper">
                          <input
                            type="number"
                            placeholder="0.00"
                            value={newKatlamaliMekanizma.cost}
                            onChange={(e) => setNewKatlamaliMekanizma({ ...newKatlamaliMekanizma, cost: e.target.value })}
                          />
                          <span className="currency-symbol">₺</span>
                        </div>
                      </div>
                      <div className="input-with-small-label" style={{ flex: '1 1 120px' }}>
                        <small>Satış Fiyatı</small>
                        <div className="input-icon-wrapper">
                          <input
                            type="number"
                            placeholder="0.00"
                            value={newKatlamaliMekanizma.price}
                            onChange={(e) => setNewKatlamaliMekanizma({ ...newKatlamaliMekanizma, price: e.target.value })}
                          />
                          <span className="currency-symbol">₺</span>
                        </div>
                      </div>
                      <button
                        className="btn-save"
                        style={{ height: '42px', padding: '0 20px' }}
                        onClick={() => {
                          if (!newKatlamaliMekanizma.type.trim() || !newKatlamaliMekanizma.price) return;
                          const currentList = katlamaliExtras.mekanizmaList || [];
                          const newList = [...currentList, { 
                            id: Date.now().toString(), 
                            type: newKatlamaliMekanizma.type.trim(), 
                            price: parseFloat(newKatlamaliMekanizma.price),
                            cost: parseFloat(newKatlamaliMekanizma.cost || 0)
                          }];
                          setKatlamaliExtras({ ...katlamaliExtras, mekanizmaList: newList });
                          handleSaveSettings({ katlamaliMekanizmaList: newList });
                          setNewKatlamaliMekanizma({ type: '', price: '', cost: '' });
                        }}
                      >
                        <i className="fas fa-plus"></i>
                        Ekle
                      </button>
                    </div>

                    <div className="mekanizma-list-wrapper">
                      {katlamaliExtras.mekanizmaList && katlamaliExtras.mekanizmaList.length > 0 ? (
                        <div className="mekanizma-cards-grid" style={{ 
                          display: 'grid', 
                          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', 
                          gap: '12px' 
                        }}>
                          {katlamaliExtras.mekanizmaList.map((mek) => (
                            <div key={mek.id} className="mekanizma-card" style={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'center',
                              padding: '12px 16px',
                              background: '#fff',
                              border: '1px solid #e2e8f0',
                              borderRadius: '12px',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                              transition: 'all 0.2s ease'
                            }}>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <span style={{ fontSize: '14px', fontWeight: '700', color: '#1e293b' }}>{mek.type}</span>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                  <span className="cost-badge" style={{ fontSize: '11px', padding: '2px 6px' }}>M: {formatPrice(mek.cost)} ₺</span>
                                  <span className="price-badge" style={{ fontSize: '11px', padding: '2px 6px' }}>F: {formatPrice(mek.price)} ₺</span>
                                </div>
                              </div>
                              <button
                                onClick={() => {
                                  const newList = katlamaliExtras.mekanizmaList.filter(m => m.id !== mek.id);
                                  setKatlamaliExtras({ ...katlamaliExtras, mekanizmaList: newList });
                                  handleSaveSettings({ katlamaliMekanizmaList: newList });
                                }}
                                className="btn-delete-item"
                                style={{ 
                                  background: '#fff1f2', 
                                  border: 'none', 
                                  color: '#e11d48', 
                                  width: '32px', 
                                  height: '32px', 
                                  borderRadius: '8px',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  transition: 'all 0.2s ease'
                                }}
                              >
                                <i className="fas fa-trash-alt" style={{ fontSize: '12px' }}></i>
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div style={{ 
                          textAlign: 'center', 
                          padding: '30px', 
                          background: '#f8fafc', 
                          borderRadius: '12px', 
                          border: '1px dashed #e2e8f0' 
                        }}>
                          <i className="fas fa-info-circle" style={{ color: '#94a3b8', marginBottom: '8px', fontSize: '18px' }}></i>
                          <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>Henüz mekanizma tipi eklenmemiş.</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Arama Kutusu */}
        <div className="product-search-bar">
          <div className="product-search-input-wrapper">
            <i className="fas fa-search product-search-icon"></i>
            <input
              type="text"
              className="product-search-input"
              placeholder="Ürün adıyla ara..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                className="product-search-clear"
                onClick={() => setSearchQuery('')}
                title="Aramayı temizle"
              >
                <i className="fas fa-times"></i>
              </button>
            )}
          </div>
          {searchQuery && (
            <span className="product-search-result-count">
              {filteredProducts.length} sonuç bulundu
            </span>
          )}
          
          <div className="product-search-actions">
            {(isAdmin || isSatisci) && !isMontajci && (
              <button className="btn-add-product" onClick={handleAddNew}>
                <i className="fas fa-plus"></i> Yeni Ürün Ekle
              </button>
            )}
          </div>
        </div>

        {isLoading ? (
          <div className="product-loading">
            <i className="fas fa-spinner fa-spin"></i> Yükleniyor...
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="product-empty">
            <i className="fas fa-box-open"></i>
            <p>Bu kategoride henüz ürün yok.</p>
            {(isAdmin || isSatisci) && !isMontajci && (
              <button className="btn-add-product-empty" onClick={handleAddNew}>
                <i className="fas fa-plus"></i> İlk ürünü ekle
              </button>
            )}
          </div>
        ) : (
          <table className="product-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Ürün Adı</th>
                <th>Firma Adı</th>
                {(activeTypeTab === 'ahsapJaluzi') ? (
                  <>
                    <th>M. İpli (M/F)</th>
                    <th>Kurdeleli (M/F)</th>
                    <th>M. İpli Red. (M/F)</th>
                    <th>Kur. Red. (M/F)</th>
                  </>
                ) : activeTypeTab === 'rustik' ? (
                  <>
                    {/* Rustik için fiyat sütunu yok */}
                  </>
                ) : (
                  <>
                    <th>Maliyet</th>
                    <th>Birim Fiyat</th>
                  </>
                )}
                {(isAdmin || isSatisci) && !isMontajci && <th>İşlemler</th>}
              </tr>
            </thead>
            <tbody>
              {filteredProducts.map((product, index) => (
                <tr key={product.id}>
                  <td className="row-num">{index + 1}</td>
                  <td className="product-name">{product.name}</td>
                  <td className="product-company">{product.companyName || <span className="no-cost">—</span>}</td>

                  {(activeTypeTab === 'ahsapJaluzi') ? (
                    <>
                      <td className="product-price">
                        {renderAmountBadge(product.merdivenIpliMaliyet, product.currency, 'cost-badge', '0.8rem')}
                        <br />
                        {renderAmountBadge(product.merdivenIpliFiyat, product.currency, 'price-badge')}
                      </td>
                      <td className="product-price">
                        {renderAmountBadge(product.kurdeleliMaliyet, product.currency, 'cost-badge', '0.8rem')}
                        <br />
                        {renderAmountBadge(product.kurdeleliFiyat, product.currency, 'price-badge')}
                      </td>
                      <td className="product-price">
                        {renderAmountBadge(product.merdivenIpliReduktorluMaliyet, product.currency, 'cost-badge', '0.8rem')}
                        <br />
                        {renderAmountBadge(product.merdivenIpliReduktorluFiyat, product.currency, 'price-badge')}
                      </td>
                      <td className="product-price">
                        {renderAmountBadge(product.kurdelaliReduktorluMaliyet, product.currency, 'cost-badge', '0.8rem')}
                        <br />
                        {renderAmountBadge(product.kurdelaliReduktorluFiyat, product.currency, 'price-badge')}
                      </td>
                    </>
                  ) : activeTypeTab === 'rustik' ? (
                    <>
                      {/* Rustik için tablo sütunlarını boş bırakıyoruz veya hiç göstermiyoruz */}
                    </>
                  ) : (
                    <>
                      <td className="product-cost">
                        {renderAmountBadge(product.cost, product.currency, 'cost-badge')}
                      </td>
                      <td className="product-price">
                        {renderAmountBadge(product.unitPrice, product.currency, 'price-badge')}
                      </td>
                    </>
                  )}
                  {(isAdmin || isSatisci) && !isMontajci && (
                    <td className="product-actions">
                      <button
                        className="btn-edit-product"
                        onClick={() => handleEdit(product)}
                        title="Düzenle"
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                      <button
                        className="btn-delete-product"
                        onClick={() => setDeleteModal(product)}
                        title="Sil"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Mobil Kart Görünümü */}
        {filteredProducts.length > 0 && (
          <div className="product-card-list">
            {filteredProducts.map((product, index) => (
              <div key={product.id} className="product-card">
                <div className="product-card-header">
                  <span className="product-card-num">#{index + 1}</span>
                  <span className="product-card-name">
                    {product.name}
                    {product.companyName && (
                      <span className="product-card-company">{product.companyName}</span>
                    )}
                  </span>
                  {(isAdmin || isSatisci) && !isMontajci && (
                    <div className="product-actions">
                      <button
                        className="btn-edit-product"
                        onClick={() => handleEdit(product)}
                        title="Düzenle"
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                      <button
                        className="btn-delete-product"
                        onClick={() => setDeleteModal(product)}
                        title="Sil"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  )}
                </div>

                <div className="product-card-prices">
                  {activeTypeTab === 'ahsapJaluzi' ? (
                    <>
                      {renderLabeledAmountBadge('M.İpli', product.merdivenIpliFiyat, product.currency, 'price-badge')}
                      {renderLabeledAmountBadge('Kurdeleli', product.kurdeleliFiyat, product.currency, 'price-badge')}
                    </>
                  ) : activeTypeTab === 'rustik' ? (
                    <span className="product-card-type">Rustik Ürün</span>
                  ) : (
                    <>
                      {renderLabeledAmountBadge('Maliyet', product.cost, product.currency, 'cost-badge')}
                      {renderLabeledAmountBadge('Satış', product.unitPrice, product.currency, 'price-badge')}
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Ürün Ekleme/Düzenleme Formu Modal */}
      {showForm && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && handleCancel()}>
          <div className="product-form-modal">
            <div className="modal-header">
              <h3>{editingProduct ? 'Ürün Düzenle' : 'Yeni Ürün Ekle'}</h3>
              <button className="modal-close" onClick={handleCancel}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <form onSubmit={handleSave} className="product-form">
              <div className="form-group">
                <label>Ürün Tipi *</label>
                <select
                  value={formData.productType}
                  onChange={(e) => setFormData(prev => ({ ...prev, productType: e.target.value }))}
                  disabled={isSaving}
                >
                  {Object.entries(PRODUCT_TYPES).map(([key, type]) => (
                    <option key={key} value={key}>{type.label}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Ürün Adı *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="örn. Karya Düz Tül"
                  disabled={isSaving}
                  autoFocus
                />
              </div>

              <div className="form-group">
                <label>Firma Adı</label>
                <input
                  type="text"
                  value={formData.companyName}
                  onChange={(e) => setFormData(prev => ({ ...prev, companyName: e.target.value }))}
                  placeholder="örn. ABC Tekstil"
                  disabled={isSaving}
                />
              </div>

              {formData.productType !== 'rustik' && (
                <div className="form-group">
                  <label>Para Birimi</label>
                  <select
                    value={formData.currency}
                    onChange={(e) => setFormData(prev => ({ ...prev, currency: e.target.value }))}
                    disabled={isSaving}
                  >
                    <option value="TL">Türk Lirası (₺)</option>
                    <option value="USD">Dolar ($)</option>
                    <option value="EUR">Euro (€)</option>
                  </select>
                  {formData.currency !== 'TL' && (
                    <small className="currency-convert-hint">
                      Güncel kur: 1 {formData.currency} ≈ {formatPrice(Number(exchangeRates[formData.currency]) || 1)} ₺
                    </small>
                  )}
                </div>
              )}

              {(formData.productType === 'ahsapJaluzi') ? (
                <div className="jaluzi-price-grid">
                  <h5 className="section-subtitle">
                    Dinamik Fiyatlandırma ({getCurrencySymbol(formData.currency)})
                  </h5>
                  <div className="jaluzi-grid-row form-row">
                    <div className="form-group">
                      <label>Maliyeti</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.merdivenIpliMaliyet}
                        onChange={(e) => setFormData(prev => ({ ...prev, merdivenIpliMaliyet: e.target.value }))}
                        disabled={isSaving}
                      />
                      {formData.currency !== 'TL' && formData.merdivenIpliMaliyet && (
                        <small className="currency-convert-hint">≈ {formatPrice(convertToTRY(formData.merdivenIpliMaliyet, formData.currency))} ₺</small>
                      )}
                    </div>
                    <div className="form-group">
                      <label>Merdiven İpli Satış Fiyatı</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.merdivenIpliFiyat}
                        onChange={(e) => setFormData(prev => ({ ...prev, merdivenIpliFiyat: e.target.value }))}
                        disabled={isSaving}
                      />
                      {formData.currency !== 'TL' && formData.merdivenIpliFiyat && (
                        <small className="currency-convert-hint">≈ {formatPrice(convertToTRY(formData.merdivenIpliFiyat, formData.currency))} ₺</small>
                      )}
                    </div>
                  </div>

                  <div className="jaluzi-grid-row form-row">
                    <div className="form-group">
                      <label>Maliyeti</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.kurdeleliMaliyet}
                        onChange={(e) => setFormData(prev => ({ ...prev, kurdeleliMaliyet: e.target.value }))}
                        disabled={isSaving}
                      />
                      {formData.currency !== 'TL' && formData.kurdeleliMaliyet && (
                        <small className="currency-convert-hint">≈ {formatPrice(convertToTRY(formData.kurdeleliMaliyet, formData.currency))} ₺</small>
                      )}
                    </div>
                    <div className="form-group">
                      <label>Kurdeleli Satış Fiyatı</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.kurdeleliFiyat}
                        onChange={(e) => setFormData(prev => ({ ...prev, kurdeleliFiyat: e.target.value }))}
                        disabled={isSaving}
                      />
                      {formData.currency !== 'TL' && formData.kurdeleliFiyat && (
                        <small className="currency-convert-hint">≈ {formatPrice(convertToTRY(formData.kurdeleliFiyat, formData.currency))} ₺</small>
                      )}
                    </div>
                  </div>

                  <div className="jaluzi-grid-row form-row">
                    <div className="form-group">
                      <label>Maliyeti</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.merdivenIpliReduktorluMaliyet}
                        onChange={(e) => setFormData(prev => ({ ...prev, merdivenIpliReduktorluMaliyet: e.target.value }))}
                        disabled={isSaving}
                      />
                      {formData.currency !== 'TL' && formData.merdivenIpliReduktorluMaliyet && (
                        <small className="currency-convert-hint">≈ {formatPrice(convertToTRY(formData.merdivenIpliReduktorluMaliyet, formData.currency))} ₺</small>
                      )}
                    </div>
                    <div className="form-group">
                      <label>Merdiven İpli Redüktörlü Satış Fiyatı</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.merdivenIpliReduktorluFiyat}
                        onChange={(e) => setFormData(prev => ({ ...prev, merdivenIpliReduktorluFiyat: e.target.value }))}
                        disabled={isSaving}
                      />
                      {formData.currency !== 'TL' && formData.merdivenIpliReduktorluFiyat && (
                        <small className="currency-convert-hint">≈ {formatPrice(convertToTRY(formData.merdivenIpliReduktorluFiyat, formData.currency))} ₺</small>
                      )}
                    </div>
                  </div>

                  <div className="jaluzi-grid-row form-row">
                    <div className="form-group">
                      <label>Maliyeti</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.kurdelaliReduktorluMaliyet}
                        onChange={(e) => setFormData(prev => ({ ...prev, kurdelaliReduktorluMaliyet: e.target.value }))}
                        disabled={isSaving}
                      />
                      {formData.currency !== 'TL' && formData.kurdelaliReduktorluMaliyet && (
                        <small className="currency-convert-hint">≈ {formatPrice(convertToTRY(formData.kurdelaliReduktorluMaliyet, formData.currency))} ₺</small>
                      )}
                    </div>
                    <div className="form-group">
                      <label>Kurdelalı Redüktörlü Satış Fiyatı</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.kurdelaliReduktorluFiyat}
                        onChange={(e) => setFormData(prev => ({ ...prev, kurdelaliReduktorluFiyat: e.target.value }))}
                        disabled={isSaving}
                      />
                      {formData.currency !== 'TL' && formData.kurdelaliReduktorluFiyat && (
                        <small className="currency-convert-hint">≈ {formatPrice(convertToTRY(formData.kurdelaliReduktorluFiyat, formData.currency))} ₺</small>
                      )}
                    </div>
                  </div>
                </div>
              ) : formData.productType === 'rustik' ? (
                <div className="rustik-info-msg" style={{ marginBottom: '15px', color: '#64748b', fontSize: '0.85rem' }}>
                  <i className="fas fa-info-circle"></i> Rustik ürünleri için fiyatlandırma, sipariş sırasında ayak ve aksesuarlar üzerinden otomatik hesaplanır. Burada sadece ürün adı girilmelidir.
                </div>
              ) : (
                <div className="form-row">
                  <div className="form-group">
                    <label>Maliyet ({getCurrencySymbol(formData.currency)})</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.cost}
                      onChange={(e) => setFormData(prev => ({ ...prev, cost: e.target.value }))}
                      placeholder="0.00"
                      disabled={isSaving}
                    />
                    {formData.currency !== 'TL' && formData.cost && (
                      <small className="currency-convert-hint">≈ {formatPrice(convertToTRY(formData.cost, formData.currency))} ₺</small>
                    )}
                  </div>

                  <div className="form-group">
                    <label>Birim Fiyat (Satış - {getCurrencySymbol(formData.currency)}) *</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.unitPrice}
                      onChange={(e) => setFormData(prev => ({ ...prev, unitPrice: e.target.value }))}
                      placeholder="0.00"
                      disabled={isSaving}
                    />
                    {formData.currency !== 'TL' && formData.unitPrice && (
                      <small className="currency-convert-hint">≈ {formatPrice(convertToTRY(formData.unitPrice, formData.currency))} ₺</small>
                    )}
                  </div>
                </div>
              )}

              {formError && (
                <div className="form-error">
                  <i className="fas fa-exclamation-circle"></i> {formError}
                </div>
              )}
              {formSuccess && (
                <div className="form-success">
                  <i className="fas fa-check-circle"></i> {formSuccess}
                </div>
              )}

              <div className="form-actions">
                <button type="button" className="btn-cancel" onClick={handleCancel} disabled={isSaving}>
                  İptal
                </button>
                <button type="submit" className="btn-save" disabled={isSaving}>
                  {isSaving ? (
                    <><i className="fas fa-spinner fa-spin"></i> Kaydediliyor...</>
                  ) : (
                    <><i className="fas fa-save"></i> {editingProduct ? 'Güncelle' : 'Kaydet'}</>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Silme Onay Modal */}
      {deleteModal && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setDeleteModal(null)}>
          <div className="delete-modal">
            <div className="modal-header">
              <h3>Ürün Sil</h3>
              <button className="modal-close" onClick={() => setDeleteModal(null)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="delete-modal-body">
              <i className="fas fa-exclamation-triangle warning-icon"></i>
              <p>
                <strong>"{deleteModal.name}"</strong> adlı ürünü silmek istediğinizden emin misiniz?
              </p>
              <p className="delete-warning">Bu işlem geri alınamaz.</p>
            </div>
            <div className="delete-modal-actions">
              <button className="btn-cancel" onClick={() => setDeleteModal(null)}>
                İptal
              </button>
              <button className="btn-delete-confirm" onClick={handleDelete}>
                <i className="fas fa-trash"></i> Sil
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Excel İşlemleri Modal */}
      {excelModal.show && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setExcelModal({ show: false })}>
          <div className="excel-modal">
            <div className="modal-header">
              <h3>{excelModal.title}</h3>
              <button className="modal-close" onClick={() => setExcelModal({ show: false })}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="excel-modal-body">
              {excelModal.type === 'error' && <i className="fas fa-times-circle error-icon"></i>}
              {excelModal.type === 'success' && <i className="fas fa-check-circle success-icon"></i>}
              {excelModal.type === 'confirm' && <i className="fas fa-exclamation-triangle warning-icon"></i>}
              {excelModal.type === 'info' && <i className="fas fa-info-circle info-icon"></i>}
              {excelModal.type === 'confirm' && <h4>Onay Gerekiyor</h4>}
              <p style={{ whiteSpace: 'pre-line' }}>{excelModal.message}</p>
            </div>
            <div className="excel-modal-actions">
              {excelModal.type === 'confirm' ? (
                <>
                  <button className="btn-cancel" onClick={() => { setExcelModal({ show: false }); setIsExcelLoading(false); }}>
                    İptal
                  </button>
                  <button className="btn-save" onClick={excelModal.onConfirm}>
                    Evet, Yükle
                  </button>
                </>
              ) : (
                <button className="btn-save" onClick={() => setExcelModal({ show: false })}>
                  Tamam
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductList;
